import { useEffect } from 'react'

import { Code } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { civilDateToProtoDate, todayCivilDate } from '~/models/civilDate'
import { BusinessMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/components/BusinessMasterFormFields'
import { useCreateBusinessMaster } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/hooks/useCreateBusinessMaster'
import {
  BusinessMasterFormSchema,
  type BusinessMasterFormValues,
} from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/models/schema'
import { resolveBusinessMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/models/serverError'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const BUSINESS_MASTER_CREATE_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.AlreadyExists]:
    'page:adminOcctoMaster.businessMaster.serverError.alreadyExists',
  [Code.Aborted]: 'page:adminOcctoMaster.businessMaster.serverError.aborted',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.businessMaster.serverError.invalidArgument',
}

/**
 * 新規登録モーダル。
 *
 * `useCrudModalForm` + `CrudModalLayout` に集約 (他 7 タブと同 pattern)。
 * server error mapping は `customErrorKeys` 経由。
 */
export const CreateBusinessMasterModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreateBusinessMaster()

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    setValue,
    isDirty,
  } = useCrudModalForm<BusinessMasterFormValues>({
    schema: BusinessMasterFormSchema,
    defaultValues: {
      businessCode: '',
      businessName: '',
      licenseTypes: [],
      purpose: 1, // BusinessPurpose.NORMAL
      validFrom: todayCivilDate(),
      validTo: undefined,
      registrationNumber: '',
    },
    onSubmit: async (values) => {
      await mutateAsync({
        businessCode: values.businessCode,
        businessName: values.businessName,
        licenseTypes: values.licenseTypes,
        purpose: values.purpose,
        validFrom: civilDateToProtoDate(values.validFrom),
        validTo:
          values.validTo !== undefined
            ? civilDateToProtoDate(values.validTo)
            : undefined,
        registrationNumber:
          values.registrationNumber !== ''
            ? values.registrationNumber
            : undefined,
      })
    },
    onClose,
    customErrorKeys: BUSINESS_MASTER_CREATE_ERROR_KEYS,
    unknownErrorKey: 'page:adminOcctoMaster.businessMaster.serverError.unknown',
    resolveDetailedError: resolveBusinessMasterDetailedError,
  })

  // モーダルオープン時に validFrom を実行日に更新（defaultValues の todayCivilDate() は
  // コンポーネントマウント時に固定化されるため、日跨ぎで開き直しても前日のままになる）。
  useEffect(() => {
    if (isOpen) {
      setValue('validFrom', todayCivilDate())
    }
  }, [isOpen, setValue])

  return (
    <CrudModalLayout
      handleSave={handleSave}
      // 入力途中で閉じようとした時に離脱確認ダイアログを出す。
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      serverError={serverError}
      title={t('page:adminOcctoMaster.businessMaster.modal.createTitle')}
    >
      <BusinessMasterFormFields
        control={control}
        errors={errors}
        isEdit={false}
      />
    </CrudModalLayout>
  )
}
